import { useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TipEarningsIssue } from "../../types";
import { dateFormatter } from "../../utils/format";

interface TipEarningsIssueIconProps {
  issue: TipEarningsIssue | null | undefined;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 720,
  maxHeight: "80vh",
  overflowY: "auto" as const,
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

function WarningTriangleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.335-.213 2.986-1.742 2.986H3.48c-1.53 0-2.493-1.651-1.743-2.986L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V8a1 1 0 112 0v3a1 1 0 01-1 1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export function TipEarningsIssueIcon({ issue }: TipEarningsIssueIconProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!issue || issue.status !== "active") return null;

  return (
    <>
      <button
        type="button"
        title={t("payments.earnings-issue.icon-title")}
        aria-label={t("payments.earnings-issue.icon-title")}
        className="inline-flex shrink-0 cursor-pointer text-amber-500 hover:text-amber-600"
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        <WarningTriangleIcon className="w-4 h-4" />
      </button>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        aria-labelledby="earnings-issue-modal"
      >
        <Box sx={modalStyle}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-6 px-2">
            <h1 id="earnings-issue-modal">{t("payments.earnings-issue.modal-title")}</h1>
          </div>
          <div className="p-6 space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <strong>{t("payments.earnings-issue.status-label")}:</strong>{" "}
              {t("payments.earnings-issue.status-pending")}
            </div>

            <div>
              <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">
                {t("payments.earnings-issue.message-label")}
              </label>
              <div className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-800">
                {issue.message}
              </div>
              <p className="mt-3 text-sm font-bold text-gray-900">
                {issue.manualTipsProccess
                  ? t("payments.earnings-issue.manual-reminder")
                  : t("payments.earnings-issue.automatic-reminder")}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <label className="text-xs font-medium text-gray-500 uppercase">
                {t("payments.earnings-issue.last-email")}
              </label>
              <p className="mt-1 text-sm font-semibold text-gray-900">
                {issue.lastEmailSentAt
                  ? dateFormatter(issue.lastEmailSentAt, { time: true })
                  : "-"}
              </p>
            </div>

            <div className="flex justify-center pt-2">
              <Button
                onClick={() => setIsOpen(false)}
                sx={{
                  color: "#898989",
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  fontWeight: "400",
                }}
              >
                {t("payments.earnings-issue.close")}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
